/* ⚠️ Charl, 14 Oct 2026: "Often clients give us an expected date of when they want their orders ... the question needs to
   be posed to them: by when do you realistically expect this order? ... can you make the factory board so that it
   automatically prioritizes orders as they come in based on the date selected on them?"
   He chose the three answers - Standard / Today, urgent / Pick a date - and an urgent fee that is BUILT BUT OFF until he
   switches it on in Parameters ("20% of the order, never less than $50" to start; both editable).
   "Late in the afternoon, and they pick the following day" counts as urgent too: after the cut-off (default 2pm) the
   standard date moves out a working day, so asking for tomorrow is then asking for faster than standard.
   Pure functions - the order form, the Factory Board and the printed job sheet all use these, so they cannot disagree.
   Dates are the shop's own calendar days, "YYYY-MM-DD", worked out in the browser's local (NZ) time. */

export const NEED_DEFAULTS = { urgentFeeOn: false, urgentFeePct: 20, urgentFeeMin: 50, urgentCutoffHour: 14, workSaturday: false, standardDays: 1 };
export function needParams(p) {
  const o = Object.assign({}, NEED_DEFAULTS);
  Object.keys(NEED_DEFAULTS).forEach(k => { if (p && p[k] !== undefined && p[k] !== null && p[k] !== "") o[k] = p[k]; });
  o.urgentFeeOn = o.urgentFeeOn === true; o.workSaturday = o.workSaturday === true;
  ["urgentFeePct", "urgentFeeMin", "urgentCutoffHour", "standardDays"].forEach(k => { o[k] = Number(o[k]); if (!isFinite(o[k])) o[k] = NEED_DEFAULTS[k]; });
  return o;
}
const pad = n => String(n).padStart(2, "0");
export const ymd = d => d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
export const fromYmd = s => { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || "")); return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null; };
const dayStart = d => new Date(d.getFullYear(), d.getMonth(), d.getDate());
export function isWorkDay(d, p) { const w = d.getDay(); return w >= 1 && w <= 5 ? true : (w === 6 && needParams(p).workSaturday); }
export function nextWorkDay(d, p) { let x = dayStart(d); do { x = new Date(x.getFullYear(), x.getMonth(), x.getDate() + 1); } while (!isWorkDay(x, p)); return x; }

/* Standard: the first working day it can be taken as placed (that day if it is a working day before the cut-off,
   otherwise the next working day), then `standardDays` working days on. Wed 10am -> Thu. Wed 3pm -> Fri. */
export function standardDate(placed, p) {
  const P = needParams(p), t = placed instanceof Date ? placed : new Date(placed);
  let base = dayStart(t);
  if (!isWorkDay(base, P) || t.getHours() >= P.urgentCutoffHour) base = nextWorkDay(base, P);
  let d = base; for (let i = 0; i < Math.max(0, P.standardDays); i++) d = nextWorkDay(d, P);
  return ymd(d);
}
/* urgent = wanted sooner than standard */
export function isUrgentDate(needed, placed, p) { return !!needed && needed < standardDate(placed, p); }

/* the fee, in dollars, when the switch is on: pct of the order, never less than the minimum */
export function urgentFeeFor(subtotal, p) {
  const P = needParams(p);
  if (!P.urgentFeeOn) return 0;
  const s = Math.max(0, Number(subtotal) || 0);
  return +Math.max(P.urgentFeeMin, s * P.urgentFeePct / 100).toFixed(2);
}

/* what an order is needed by: the date asked for, or (orders from before this existed) standard from when it was placed */
export function neededOf(o, p) {
  const jd = (o && o.jobDetails) || {};
  if (fromYmd(jd.neededBy)) return { date: jd.neededBy, asked: true, urgent: !!jd.urgent || (o.createdAt ? isUrgentDate(jd.neededBy, o.createdAt, p) : false) };
  const at = (o && (o.createdAt || o.placedAt)) || null;
  return { date: at ? standardDate(at, p) : null, asked: false, urgent: false };
}
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"], MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function niceDate(s) { const d = fromYmd(s); return d ? DOW[d.getDay()] + " " + d.getDate() + " " + MON[d.getMonth()] : ""; }
/* the words and colour on the board: overdue / today / tomorrow (next working day) / later */
export function dueTone(s, now, p) {
  const t = ymd(now || new Date());
  if (!s) return { tone: "none", words: "" };
  if (s < t) return { tone: "overdue", words: "OVERDUE · " + niceDate(s) };
  if (s === t) return { tone: "today", words: "TODAY" };
  if (s === ymd(nextWorkDay(fromYmd(t), p)) || s === ymd(new Date(fromYmd(t).getFullYear(), fromYmd(t).getMonth(), fromYmd(t).getDate() + 1))) return { tone: "tomorrow", words: "Tomorrow · " + niceDate(s) };
  return { tone: "later", words: niceDate(s) };
}
export const TONE_COLOUR = { overdue: "#dc2626", today: "#dc2626", tomorrow: "#e8a020", later: "#9a86a8", none: "#9a86a8" };
/* most pressing first; the same day -> the one placed first */
export function byNeeded(p) {
  return (a, b) => {
    const A = neededOf(a, p).date || "9999", B = neededOf(b, p).date || "9999";
    if (A !== B) return A < B ? -1 : 1;
    return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
  };
}
